import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, CalendarDays } from 'lucide-react';
import { Button } from '../ui/button';
import { HeroBase, HeroCommonExtras, AnimatedHeadlineWords, cmsUrl } from './HeroBase';

// Story-cards hero — copy on the left, tilted 2×2 polaroid collage on the right.
const ROTATIONS = ['-3deg', '4deg', '4deg', '-3deg'];

export const HeroStoryCards = ({ cms = {}, onPrimary, onOpenAvailability }) => {
  const v = cms.variants?.story_cards || {};
  const panels = (Array.isArray(v.panels) ? v.panels : []).slice(0, 4);
  const primaryCta = cms.primary_cta_text   || 'Build Your Dream Party';
  const secondCta  = cms.secondary_cta_text || 'Check Availability';
  const extras = HeroCommonExtras({ cms });

  return (
    <HeroBase
      cms={cms}
      fallbackBg="#FFFDF8"
      className="py-12 sm:py-20"
      testidVariant="story_cards"
    >
      <div className="container mx-auto px-4 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center relative z-10">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#FF3B6B] mb-4" data-testid="hero-badge">
            {cms.badge_text || 'Creative Birthdays · Oakville'}
          </span>
          <AnimatedHeadlineWords
            cms={cms}
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] mb-5"
          />
          {extras.sub}
          {extras.location}
          {extras.pills({ when: 'above' })}
          <div className="flex flex-wrap gap-3">
            <Button onClick={onPrimary} size="lg" className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white px-7" data-testid="hero-primary-cta">
              <Calendar className="w-5 h-5 mr-2" /> {primaryCta}
            </Button>
            <Button onClick={onOpenAvailability} variant="outline" size="lg" className="rounded-full px-7" data-testid="hero-secondary-cta">
              <CalendarDays className="w-5 h-5 mr-2" /> {secondCta}
            </Button>
          </div>
          {extras.pills({ when: 'below' })}
          {extras.trustBadges}
        </motion.div>

        <div className="relative grid grid-cols-2 gap-4 max-w-md mx-auto w-full">
          {panels.map((p, i) => (
            <motion.figure
              key={p.id || i}
              initial={{ opacity: 0, y: 24, rotate: 0 }}
              animate={{ opacity: 1, y: 0, rotate: ROTATIONS[i % ROTATIONS.length] }}
              whileHover={{ rotate: '0deg', scale: 1.03 }}
              transition={{ duration: 0.4, delay: 0.1 + i * 0.08, ease: 'easeOut' }}
              className="rounded-md p-2 pb-7 shadow-xl border border-[#1E1E24]/5"
              style={{ background: p.bg || '#FFEFF5' }}
              data-testid={`hero-story-card-${i}`}
            >
              <div className="aspect-square w-full overflow-hidden rounded-sm bg-white">
                {p.image_url ? (
                  <img src={cmsUrl(p.image_url)} alt={p.caption || `Story ${i + 1}`} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl" aria-hidden>📸</div>
                )}
              </div>
              {p.caption && (
                <figcaption className="text-xs text-center mt-2 text-[#1E1E24]" style={{ fontFamily: '"Caveat", cursive' }}>
                  {p.caption}
                </figcaption>
              )}
            </motion.figure>
          ))}
        </div>
      </div>
    </HeroBase>
  );
};

export default HeroStoryCards;
